/**
 * Export handlers for the impact-measurement screens (G2.4, G2 test *d*; CSV and Excel, ADR 9010 as amended by DEC-98).
 *
 * Each re-reads the SAME operations its screen reads, over the same perimeter, and builds the
 * same rows through the same pure functions (`impact.ts`, `control.ts`) — so the file and the
 * screen cannot disagree about a figure. An absent figure leaves as `missing`, never `0`.
 */

import { MAX_PAGES, callOperation, collectPages } from '@/api';
import {
  getMonthlyReport, listConsolidationLines, listConsolidationRuns, listD4Evidence, listMonthlyReportMonths, listPriceActions, inseasonListRuns,
  type D4EvidenceRow, type MonthlyReport,
} from '@/api/generated';
import { NOT_PUBLISHED, NO_PRINCIPAL, buildVersion } from '@/export';
import type { ExportCell, ExportColumn, ExportRequest, Provenance, ProvenanceFilter } from '@/export';
import type { ExportScope } from '../manifest';

import {
  inPerimeter, pairDelta, pairReasonCodes, pairVerdict, readControlScope,
  type ControlScope,
} from './control';
import { CONSOLIDATION_RUN_JOB, IMPACT_RUN_LIMIT, PRICE_ACTION_PAGE_ROWS } from './hooks';
import { readPackMonthPath } from './monthly-pack-route';
import { PACK_HEALTH_COLUMNS, packHealthCells } from './pack-health';
import { PACK_MONTHS_PROBED, isNotFrozen, monthWindow, packMonthRows, readPackScope } from './programme';
import {
  EXIT_LANE_COUNT_QUERIES, IMPACT_ABSENCE, LINE_PAGE_ROWS, exitRunImpact, exitRunNotTotalled, exitRunTooLarge,
  inseasonRunImpact, laneCountsOf, parseDecimal, type ExitRunImpact, type ImpactFigure, type InseasonRunImpact,
} from './impact';
import { exportDisplay } from '@/planning/words';

const PLATFORM_VERSION = buildVersion();

/** The export ids, one per route — `<slug>.<route>.<surface>`. */
export const IMPACT_EXPORT_ID = 'strategic.impact.table';
export const CONTROL_EXPORT_ID = 'strategic.testVsControl.table';
export const PACK_EXPORT_ID = 'strategic.monthlyPack.totals';
/** The closed months list (issue 226: the pack is its own page, `monthly-pack/:month`). */
export const PACK_MONTHS_EXPORT_ID = 'strategic.monthlyPack.months';

const missing: ExportCell = { kind: 'missing', state: 'not-reported' };

const figureCell = (figure: ImpactFigure): ExportCell =>
  figure.kind === 'measured' ? { kind: 'number', raw: figure.amount } : missing;

const currencyCell = (figure: ImpactFigure): ExportCell =>
  figure.kind === 'measured' ? { kind: 'text', value: figure.currency } : missing;

/** Why a figure is absent, as the code it is (the catalogue key), so a filter can find it. */
const reasonCell = (figure: ImpactFigure): ExportCell =>
  figure.kind === 'measured' ? missing : { kind: 'label', value: figure.reasonKey, code: figure.reasonKey };

const numberCell = (value: number | null | undefined): ExportCell =>
  value === null || value === undefined ? missing : { kind: 'number', raw: value };

const integerCell = (value: number | null | undefined): ExportCell =>
  value === null || value === undefined ? missing : { kind: 'integer', raw: value };

function provenance(
  measures: readonly { readonly measure: string; readonly definition: string }[],
  filters: readonly ProvenanceFilter[],
): Provenance {
  return {
    filters,
    hierarchyNode: NOT_PUBLISHED,
    // Runs carry timestamps, not a declared window; a window drawn from them would be a
    // claim about a perimeter nobody asked for.
    timeWindow: NOT_PUBLISHED,
    scenario: NOT_PUBLISHED,
    planVersionId: NOT_PUBLISHED,
    measureDefinitions: measures.map((entry) => ({ ...entry, registerVersion: '' })),
    generatedAt: new Date().toISOString(),
    generatedBy: NO_PRINCIPAL,
    platformVersion: PLATFORM_VERSION,
  };
}

const RECOVERY_DEFINITION = {
  measure: 'inseason.expected_margin_recovery',
  definition: 'The sum of expected_margin_recovery over one cycle run, once per recommended PATH (article x cluster in the run), in their one currency. The field is the total contribution of the whole path and is repeated on every price step of that path, so it is never summed over steps. Expected, not realised. Undefined — never zero — for a run with no price step or whose paths span currencies.',
} as const;

const NET_BENEFIT_DEFINITION = {
  measure: 'exit.consolidation_net_benefit',
  definition: 'The sum of net_benefit_amount over one consolidation run\'s proposed lanes that publish lane cost, incremental revenue and net benefit, in their one currency. Undefined — never zero — for a run with no such lane or whose lanes span currencies.',
} as const;

// ── /strategic/impact ─────────────────────────────────────────────────────────

/** The impact sheet over readings already made: one row per module × run. */
export function impactRequest(
  inseason: readonly InseasonRunImpact[],
  exit: readonly ExitRunImpact[],
): ExportRequest {
  const rows: ExportCell[][] = [
    ...inseason.map((run): ExportCell[] => [
      { kind: 'label', value: 'inseason', code: 'inseason' },
      { kind: 'text', value: run.runId },
      { kind: 'text', value: run.generatedAt },
      { kind: 'text', value: run.countryKey },
      { kind: 'label', value: 'expected_margin_recovery', code: 'expected_margin_recovery' },
      figureCell(run.expectedMarginRecovery),
      currencyCell(run.expectedMarginRecovery),
      // The rows the figure is summed over are PATHS, each once — not the run's price steps.
      integerCell(run.pricePaths),
      reasonCell(run.expectedMarginRecovery),
    ]),
    ...exit.map((run): ExportCell[] => [
      { kind: 'label', value: 'exit', code: 'exit' },
      { kind: 'text', value: run.runId },
      { kind: 'text', value: run.recordedAt },
      { kind: 'text', value: run.countryKey },
      { kind: 'label', value: 'net_benefit_amount', code: 'net_benefit_amount' },
      figureCell(run.netBenefit),
      currencyCell(run.netBenefit),
      // The rows the amount is summed over: none where the run was not totalled (the lane counts are not rows summed).
      integerCell(run.totalled ? run.pricedLanes : null),
      reasonCell(run.netBenefit),
    ]),
  ];
  return {
    id: IMPACT_EXPORT_ID,
    titleKey: 'strategic.impact.title',
    columns: [
      { key: 'module', headerKey: 'strategic.column.module', kind: 'label', descriptionKey: 'strategic.columnGuide.impact.module' },
      { key: 'run_id', headerKey: 'strategic.column.run', kind: 'text', descriptionKey: 'strategic.columnGuide.impact.runId' },
      { key: 'recorded_at', headerKey: 'strategic.column.recordedAt', kind: 'text', descriptionKey: 'strategic.columnGuide.impact.recordedAt' },
      { key: 'country_key', headerKey: 'strategic.column.country', kind: 'text', descriptionKey: 'strategic.columnGuide.country' },
      { key: 'measure', headerKey: 'strategic.column.measure', kind: 'label', descriptionKey: 'strategic.columnGuide.impact.measure' },
      // A number beside its own currency column rather than a `money` column: a money column
      // states ONE currency, and these rows are each in their own run's.
      { key: 'amount', headerKey: 'strategic.column.amount', kind: 'number', polarity: 'higher-is-better', descriptionKey: 'strategic.columnGuide.impact.amount' },
      { key: 'currency_code', headerKey: 'strategic.column.currency', kind: 'text', descriptionKey: 'strategic.columnGuide.currency' },
      { key: 'rows_summed', headerKey: 'strategic.column.rowsSummed', kind: 'integer', descriptionKey: 'strategic.columnGuide.impact.rowsSummed' },
      // The cell carries the catalogue key itself: the workbook says its words (audit E2).
      { key: 'absent_because', headerKey: 'strategic.column.absentBecause', kind: 'label', display: exportDisplay((code) => code), descriptionKey: 'strategic.columnGuide.impact.absentBecause' },
    ],
    rows: () => rows,
    rowCount: rows.length,
    provenance: provenance([RECOVERY_DEFINITION, NET_BENEFIT_DEFINITION], [
      { field: 'runs', operator: 'newest', value: String(IMPACT_RUN_LIMIT) },
      { field: 'job_name', operator: 'equals', value: CONSOLIDATION_RUN_JOB },
    ]),
    perimeter: {
      filters: [],
      sort: [{ key: 'module', direction: 'asc' }],
      visibleColumns: ['module', 'run_id', 'recorded_at', 'country_key', 'measure', 'amount', 'currency_code', 'rows_summed', 'absent_because'],
      drillPath: [],
      densityMode: 'compact',
    },
  };
}

/** The route's resolver: the screen's reads, re-asked. */
export async function impactExport(): Promise<ExportRequest> {
  // ONE page of each directory — the newest `IMPACT_RUN_LIMIT` runs, as the screen reads them.
  // `collectPages` here would walk the whole history and total runs the screen never showed.
  const [directory, exitDirectory] = await Promise.all([
    callOperation(inseasonListRuns, { query: { limit: IMPACT_RUN_LIMIT } }).then((page) => page.items),
    callOperation(listConsolidationRuns, { query: { job_name: CONSOLIDATION_RUN_JOB, limit: IMPACT_RUN_LIMIT } })
      .then((page) => page.items),
  ]);
  const inseason = await Promise.all(directory.map(async (run) => inseasonRunImpact(
    run, await collectPages(listPriceActions, { query: { run_id: run.run_id, limit: PRICE_ACTION_PAGE_ROWS } }),
  )));
  const exit = await Promise.all(exitDirectory.map(async (run) => (exitRunTooLarge(run, MAX_PAGES)
    // Too large to total: the sums are absent, the lane counts are the platform's (`matching`, a page of one row each).
    ? exitRunNotTotalled(run, IMPACT_ABSENCE.tooManyLines, laneCountsOf(await Promise.all(EXIT_LANE_COUNT_QUERIES.map(
      (narrowing) => callOperation(listConsolidationLines, { query: { run_id: run.run_id, ...narrowing, limit: 1 } }),
    ))))
    : exitRunImpact(run, await collectPages(listConsolidationLines, {
      query: { run_id: run.run_id, is_proposed: true, limit: LINE_PAGE_ROWS },
    })))));
  return impactRequest(inseason, exit);
}

// ── /strategic/test-vs-control ─────────────────────────────────────────────────

/** The comparison sheet over the pairs in the perimeter. */
export function controlRequest(rows: readonly D4EvidenceRow[], scope: ControlScope): ExportRequest {
  const cells: ExportCell[][] = rows.map((row) => [
    { kind: 'text', value: row.season_code },
    { kind: 'text', value: row.country_key },
    { kind: 'text', value: row.option_id },
    { kind: 'text', value: row.cluster_id },
    numberCell(parseDecimal(row.shadow_realised_price)),
    numberCell(parseDecimal(row.incumbent_realised_price)),
    // Never `?? 0` — a not-evaluable pair's delta is not a zero improvement.
    numberCell(pairDelta(row)),
    integerCell(row.shadow_terminal_units),
    integerCell(row.incumbent_terminal_units),
    row.at_constant_terminal_stock === null || row.at_constant_terminal_stock === undefined
      ? missing
      : { kind: 'label', value: String(row.at_constant_terminal_stock), code: String(row.at_constant_terminal_stock) },
    { kind: 'label', value: pairVerdict(row), code: pairVerdict(row) },
    { kind: 'text', value: pairReasonCodes(row).join(',') },
    { kind: 'text', value: row.markdown_variant_id },
  ]);
  const filters: ProvenanceFilter[] = [{ field: 'gate', operator: 'equals', value: 'D4' }];
  if (scope.season !== null) filters.push({ field: 'season_code', operator: 'equals', value: scope.season });
  if (scope.country !== null) filters.push({ field: 'country_key', operator: 'equals', value: scope.country });
  return {
    id: CONTROL_EXPORT_ID,
    titleKey: 'strategic.control.title',
    columns: [
      { key: 'season_code', headerKey: 'strategic.column.season', kind: 'text', descriptionKey: 'strategic.columnGuide.control.season' },
      { key: 'country_key', headerKey: 'strategic.column.country', kind: 'text', descriptionKey: 'strategic.columnGuide.country' },
      { key: 'option_id', headerKey: 'strategic.column.option', kind: 'text', descriptionKey: 'strategic.help.testVsControlScreen.colOption' },
      { key: 'cluster_id', headerKey: 'strategic.column.cluster', kind: 'text', descriptionKey: 'strategic.help.testVsControlScreen.colCluster' },
      { key: 'test_realised_price', headerKey: 'strategic.column.testRealisedPrice', kind: 'number', polarity: 'higher-is-better', descriptionKey: 'strategic.columnGuide.control.testRealisedPrice' },
      { key: 'control_realised_price', headerKey: 'strategic.column.controlRealisedPrice', kind: 'number', polarity: 'higher-is-better', descriptionKey: 'strategic.columnGuide.control.controlRealisedPrice' },
      { key: 'realised_price_delta', headerKey: 'strategic.column.delta', kind: 'number', polarity: 'higher-is-better', descriptionKey: 'strategic.columnGuide.control.delta' },
      { key: 'test_terminal_units', headerKey: 'strategic.column.testTerminalUnits', kind: 'integer', polarity: 'lower-is-better', descriptionKey: 'strategic.columnGuide.control.testTerminalUnits' },
      { key: 'control_terminal_units', headerKey: 'strategic.column.controlTerminalUnits', kind: 'integer', polarity: 'lower-is-better', descriptionKey: 'strategic.columnGuide.control.controlTerminalUnits' },
      { key: 'at_constant_terminal_stock', headerKey: 'strategic.column.constantStock', kind: 'label', descriptionKey: 'strategic.columnGuide.control.constantStock' },
      { key: 'verdict', headerKey: 'strategic.column.verdict', kind: 'label', descriptionKey: 'strategic.columnGuide.control.verdict' },
      { key: 'not_evaluable_reason_codes', headerKey: 'strategic.column.notEvaluableBecause', kind: 'text', descriptionKey: 'strategic.columnGuide.control.notEvaluableBecause' },
      { key: 'markdown_variant_id', headerKey: 'strategic.column.variant', kind: 'text', descriptionKey: 'strategic.columnGuide.control.variant' },
    ],
    rows: () => cells,
    rowCount: cells.length,
    provenance: provenance([{
      measure: 'exit.d4_realised_price_delta',
      definition: 'Test (shadow cadence) realised price less control (incumbent) realised price, per option and cluster over a shadow season. Undefined — never zero — while the pair is not evaluable.',
    }], filters),
    perimeter: {
      filters,
      sort: [{ key: 'option_id', direction: 'asc' }],
      visibleColumns: ['season_code', 'country_key', 'option_id', 'cluster_id', 'test_realised_price', 'control_realised_price', 'realised_price_delta', 'test_terminal_units', 'control_terminal_units', 'at_constant_terminal_stock', 'verdict', 'not_evaluable_reason_codes', 'markdown_variant_id'],
      drillPath: [],
      densityMode: 'compact',
    },
  };
}

export async function controlExport(scope?: ExportScope): Promise<ExportRequest> {
  const perimeter = readControlScope(scope?.search ?? '');
  const rows = await collectPages(listD4Evidence, {
    query: {
      ...(perimeter.season === null ? {} : { season_code: perimeter.season }),
      ...(perimeter.country === null ? {} : { country_key: perimeter.country }),
    },
  });
  return controlRequest(inPerimeter(rows, perimeter), perimeter);
}

// ── /strategic/monthly-pack ─────────────────────────────────────────────────

const PACK_TOTALS_DEFINITION = {
  measure: 'programme.monthly_report.totals',
  definition: 'Per benefit family and currency, for one closed month as frozen by programme.monthly_report (bata-platform ADR 9016): recommended = the projection of every live recommendation written in the month; expected = the projection of those approved before the close (DEC-19). A markdown path is valued once, and expected only when all its steps are approved. Adjusted lines are counted, never valued; lines with no archived projection are unvaluedCount. Served as frozen, never recomputed.',
} as const;

/** The pack's totals columns — the CSV, and the workbook's first sheet. */
const PACK_TOTALS_COLUMNS: readonly ExportColumn[] = [
  { key: 'country_key', headerKey: 'strategic.column.country', kind: 'text', descriptionKey: 'strategic.columnGuide.country' },
  { key: 'month', headerKey: 'strategic.pack.column.month', kind: 'integer', descriptionKey: 'strategic.columnGuide.pack.month' },
  { key: 'family', headerKey: 'strategic.pack.column.family', kind: 'label', descriptionKey: 'strategic.help.monthlyPackScreen.colFamily' },
  { key: 'currency_code', headerKey: 'strategic.column.currency', kind: 'text', descriptionKey: 'strategic.columnGuide.currency' },
  { key: 'recommended', headerKey: 'strategic.pack.column.recommended', kind: 'number', descriptionKey: 'strategic.help.monthlyPackScreen.colRecommended' },
  { key: 'expected', headerKey: 'strategic.pack.column.expected', kind: 'number', polarity: 'higher-is-better', descriptionKey: 'strategic.help.monthlyPackScreen.colExpected' },
  { key: 'recommended_count', headerKey: 'strategic.pack.column.recommendedCount', kind: 'integer', descriptionKey: 'strategic.help.monthlyPackScreen.colRecommendedCount' },
  { key: 'approved_count', headerKey: 'strategic.pack.column.approvedCount', kind: 'integer', descriptionKey: 'strategic.help.monthlyPackScreen.colApprovedCount' },
  { key: 'adjusted_count', headerKey: 'strategic.pack.column.adjustedCount', kind: 'integer', descriptionKey: 'strategic.help.monthlyPackScreen.colAdjustedCount' },
  { key: 'unvalued_count', headerKey: 'strategic.pack.column.unvaluedCount', kind: 'integer', descriptionKey: 'strategic.help.monthlyPackScreen.colUnvaluedCount' },
];

/**
 * The pack's totals, exactly as frozen — the route's CSV. Each family × currency is one row; two
 * currencies are two rows, never a sum. The pack's own provenance travels as filters.
 */
export function monthlyPackRequest(report: MonthlyReport | null, scope: { readonly country: string | null; readonly month: number | null }): ExportRequest {
  const rows: ExportCell[][] = (report?.totals ?? []).map((total) => [
    { kind: 'text', value: report?.countryKey ?? '' },
    { kind: 'integer', raw: report?.month ?? 0 },
    { kind: 'label', value: total.family, code: total.family },
    { kind: 'text', value: total.currencyCode },
    { kind: 'number', raw: total.recommended },
    { kind: 'number', raw: total.expected },
    { kind: 'integer', raw: total.recommendedCount },
    { kind: 'integer', raw: total.approvedCount },
    { kind: 'integer', raw: total.adjustedCount },
    { kind: 'integer', raw: total.unvaluedCount },
  ]);
  const filters: ProvenanceFilter[] = [];
  if (scope.country !== null) filters.push({ field: 'country_key', operator: 'equals', value: scope.country });
  if (scope.month !== null) filters.push({ field: 'month', operator: 'equals', value: String(scope.month) });
  if (report !== null) {
    const stated = report.provenance;
    filters.push({ field: 'frozen_at', operator: 'equals', value: report.frozenAt });
    filters.push({ field: 'batch_id', operator: 'equals', value: stated.batchId });
    filters.push({ field: 'commit_sha', operator: 'equals', value: stated.commitSha });
    if (stated.parameterSetId !== null) filters.push({ field: 'parameter_set_id', operator: 'equals', value: stated.parameterSetId });
    if (stated.metricsVersion !== null) filters.push({ field: 'metrics_version', operator: 'equals', value: stated.metricsVersion });
    if (stated.windowEnd !== null) filters.push({ field: 'window_end', operator: 'equals', value: stated.windowEnd });
  }
  const base = provenance([PACK_TOTALS_DEFINITION], filters);
  return {
    id: PACK_EXPORT_ID,
    titleKey: 'strategic.pack.title',
    columns: PACK_TOTALS_COLUMNS,
    rows: () => rows,
    rowCount: rows.length,
    // The workbook carries the frozen Health section as a sheet of its own (bata-reqs docs/25 §6, ADR 3002
    // amended); the CSV stays the totals. A pack frozen before the section existed has no such sheet.
    ...(report === null || report.health === null || report.health === undefined ? {} : {
      workbook: {
        sheets: [
          { titleKey: 'strategic.pack.totalsSheet', columns: PACK_TOTALS_COLUMNS, rows: () => rows },
          { titleKey: 'strategic.pack.health.sheet', columns: PACK_HEALTH_COLUMNS, rows: () => (report.health ?? []).map(packHealthCells) },
        ],
      },
    }),
    provenance: {
      ...base,
      timeWindow: scope.month === null ? NOT_PUBLISHED : monthWindow(scope.month),
    },
    perimeter: {
      filters,
      sort: [{ key: 'family', direction: 'asc' }],
      visibleColumns: ['country_key', 'month', 'family', 'currency_code', 'recommended', 'expected', 'recommended_count', 'approved_count', 'adjusted_count', 'unvalued_count'],
      drillPath: [],
      densityMode: 'compact',
    },
  };
}

/**
 * The pack page's resolver: the frozen pack the URL names, re-read; none named or not frozen → no
 * rows. The month is the path's (`/strategic/monthly-pack/:month`, issue 226); a `?month=` still
 * names one, for a link made before the pack had its own address.
 */
export async function monthlyPackExport(scope?: ExportScope): Promise<ExportRequest> {
  const search = readPackScope(scope?.search ?? '');
  const perimeter = { country: search.country, month: readPackMonthPath(scope?.path ?? '') ?? search.month };
  if (perimeter.country === null || perimeter.month === null) return monthlyPackRequest(null, perimeter);
  try {
    const report = await callOperation(getMonthlyReport, { query: { country_key: perimeter.country, month: perimeter.month } });
    return monthlyPackRequest(report, perimeter);
  } catch (error) {
    if (isNotFrozen(error)) return monthlyPackRequest(null, perimeter);
    throw error;
  }
}

const PACK_MONTHS_DEFINITION = {
  measure: 'programme.monthly_report.frozen',
  definition: 'Whether programme.monthly_report (bata-platform ADR 9016) has frozen the pack of each of the last twelve closed months for the market, as the platform lists them (GET /programme/reports/monthly/months, ADR 9031): frozen, with when, or not frozen yet (not on the list, and not requested). The current month is never listed.',
} as const;

/** The months list's resolver: each of the last twelve closed months of the market, from the one months read the page uses. */
export async function monthlyPackMonthsExport(scope?: ExportScope): Promise<ExportRequest> {
  const { country } = readPackScope(scope?.search ?? '');
  const frozen = country === null
    ? []
    : await callOperation(listMonthlyReportMonths, { query: { country_key: country, limit: PACK_MONTHS_PROBED } });
  const rows: ExportCell[][] = (country === null ? [] : packMonthRows(frozen, new Date(), PACK_MONTHS_PROBED)).map(({ month, frozenAt }) => (
    frozenAt === null
      ? [{ kind: 'integer', raw: month }, { kind: 'label', value: 'notFrozen', code: 'notFrozen' }, missing]
      : [{ kind: 'integer', raw: month }, { kind: 'label', value: 'frozen', code: 'frozen' }, { kind: 'date', iso: frozenAt }]
  ));
  const filters: ProvenanceFilter[] = country === null ? [] : [{ field: 'country_key', operator: 'equals', value: country }];
  return {
    id: PACK_MONTHS_EXPORT_ID,
    titleKey: 'strategic.pack.monthsTitle',
    columns: [
      { key: 'month', headerKey: 'strategic.pack.column.month', kind: 'integer', descriptionKey: 'strategic.columnGuide.pack.monthsMonth' },
      { key: 'state', headerKey: 'strategic.pack.column.state', kind: 'label', descriptionKey: 'strategic.columnGuide.pack.state' },
      { key: 'frozen_at', headerKey: 'strategic.pack.constant.frozen', kind: 'date', descriptionKey: 'strategic.columnGuide.pack.frozenAt' },
    ],
    rows: () => rows,
    rowCount: rows.length,
    provenance: provenance([PACK_MONTHS_DEFINITION], filters),
    perimeter: {
      filters,
      sort: [{ key: 'month', direction: 'desc' }],
      visibleColumns: ['month', 'state', 'frozen_at'],
      drillPath: [],
      densityMode: 'compact',
    },
  };
}
